/**
 * StreamingMessage - Component for displaying streaming Q&A responses
 *
 * Features:
 * - Progressive text rendering as tokens arrive
 * - Status indicator during processing phases
 * - Inline citation detection and highlighting
 * - Cancel button for long responses
 * - Degradation warning display
 */
import React, { useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';

interface StreamingMessageProps {
  content: string;
  citations?: unknown[];  // kept for backwards compat, not rendered
  status: string;
  isStreaming: boolean;
  degradationLevel?: string | null | undefined;
  degradationWarning?: string | null | undefined;
  /**
   * Long-running tool banner. When the backend emits a ``tool_use``
   * event for a tool flagged ``long_running: True`` in TOOL_DEFINITIONS,
   * the hook surfaces the tool's pre-canned ``user_message`` here.
   * Distinct from ``status`` (per-stage progress text) — this is a
   * persistent banner that sets expectations BEFORE the long wait.
   * Hidden when null / empty.
   */
  longRunningMessage?: string | null | undefined;
  onCitationClick?: unknown;  // kept for backwards compat, not used
  onCancel?: (() => void) | undefined;
}

/**
 * Map degradation levels to user-friendly descriptions
 */
const DEGRADATION_MESSAGES: Record<string, string> = {
  compress_history: 'Using summarized conversation history',
  reduce_chunks: 'Based on top 10 most relevant passages',
  skip_historical: 'Current version only (historical excluded)',
  critical_sections: 'Based on most relevant sections',
  partial_answer: 'Partial answer due to context limits',
};

/**
 * Streaming message bubble with progressive rendering
 */
export const StreamingMessage: React.FC<StreamingMessageProps> = ({
  content,
  status,
  isStreaming,
  degradationLevel,
  degradationWarning,
  longRunningMessage,
  onCancel,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  // Auto-scroll as content streams
  useEffect(() => {
    if (contentRef.current && isStreaming) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [content, isStreaming]);

  const showDegradationWarning = degradationLevel && degradationLevel !== 'full_context';

  return (
    <div className="flex justify-start">
      <div className="bg-slate-100 text-slate-800 rounded-2xl rounded-bl-md px-4 py-3 max-w-[85%] w-full">
        {/* Long-running tool banner — sets expectations during a 30-90s
            wait when investigate_drugs (or any tool flagged long_running)
            is mid-flight. Distinct from the per-stage status indicator
            below; both can show simultaneously. */}
        {isStreaming && longRunningMessage && (
          <div className="flex items-start gap-2 bg-indigo-50 text-indigo-900 rounded-lg px-3 py-2 mb-3 text-sm border border-indigo-200">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4 mt-0.5 flex-shrink-0 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                strokeWidth="3"
                strokeDasharray="40 60"
                strokeLinecap="round"
              />
            </svg>
            <span className="flex-1">{longRunningMessage}</span>
          </div>
        )}
        {/* Status indicator */}
        {/* The status line IS the progress indicator (animated dots +
            "Planning next step..." / "Searching labels..."); the generic
            "Thinking..." placeholder below only shows when no status has
            arrived yet (user, 2026-11-04: two progress rows added nothing). */}
        {isStreaming && status && (
          <div
            data-testid="streaming-status"
            className={`flex items-center gap-2 text-slate-500 ${content ? 'mb-2 pb-2 border-b border-slate-200' : ''}`}
          >
            <div className="flex gap-1" aria-hidden="true">
              <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
              <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
              <span className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
            </div>
            <span className="text-sm">{status}</span>
          </div>
        )}

        {/* Degradation warning */}
        {showDegradationWarning && (
          <div className="flex items-start gap-2 bg-amber-50 text-amber-800 rounded-lg px-3 py-2 mb-3 text-sm">
            <svg
              className="w-4 h-4 mt-0.5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>
              {degradationWarning ||
                DEGRADATION_MESSAGES[degradationLevel] ||
                'Context was reduced to fit budget'}
            </span>
          </div>
        )}

        {/* Streaming content */}
        <div
          ref={contentRef}
          className="prose prose-sm prose-slate max-w-none"
        >
          {content ? (
            <div
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content, { USE_PROFILES: { html: true } }) }}
            />
          ) : isStreaming && !status ? (
            <div className="flex items-center gap-2 text-slate-500">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-sm">Thinking...</span>
            </div>
          ) : null}

          {/* Cursor indicator while streaming */}
          {isStreaming && content && (
            <span className="inline-block w-2 h-4 bg-indigo-500 animate-pulse ml-0.5" />
          )}
        </div>

        {/* Cancel button */}
        {isStreaming && onCancel && (
          <div className="mt-3 pt-2 border-t border-slate-200">
            <button
              onClick={onCancel}
              className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StreamingMessage;
